/**
 * §7.2 — "Projects: table of all projects, client as a column; a row opens the
 * project."
 *
 * This is the navigation entry §1.2 says is missing. The space tree is still
 * there and still correct; what it cannot do is answer "which projects are
 * running", because a project was never a row — only a space whose name happened
 * to be one.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  useCreateProject,
  useOrganizations,
  usePeoplePicker,
  useProjects,
} from '../../api/queries.ts';
import type { CreateProjectResponse } from '../../api/types.ts';
import { formatDate } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

const STATUS_TONES = {
  active: 'success',
  on_hold: 'warning',
  complete: 'gray',
  archived: 'gray',
} as const;

function CreateProjectForm({ onClose }: { onClose: () => void }) {
  const create = useCreateProject();
  const organizations = useOrganizations();
  const people = usePeoplePicker();
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [leadId, setLeadId] = useState('');
  const [result, setResult] = useState<CreateProjectResponse | null>(null);

  // Only organizations with a space can hold a project — §5.4's trigger requires
  // the project's space to be a child of theirs, and a prospect has none.
  const organizationOptions = (organizations.data?.organizations ?? [])
    .filter((organization) => organization.space !== null)
    .map((organization) => ({ value: organization.id, label: organization.name }));

  const leadOptions = [
    { value: '', label: 'No lead yet' },
    ...(people.data?.people ?? []).map((person) => ({ value: person.id, label: person.name })),
  ];

  if (result) {
    return (
      <>
        <DialogBody>
          <Alert tone="success" title="One transaction, and nobody else was granted anything">
            <p>
              {result.project.name} exists, with its own space
              {result.sharedSpace ? ' and the portal home beneath it' : ''}. Creating it widened
              nobody's access — not even the client contacts already on this organization's shared
              space. Adding them is one deliberate action per person.
            </p>
          </Alert>
        </DialogBody>
        <DialogFooter>
          <Link
            to="/projects/$projectId"
            params={{ projectId: result.project.id }}
            className="no-underline"
          >
            <Button variant="primary">Open the project</Button>
          </Link>
        </DialogFooter>
      </>
    );
  }

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            name: name.trim(),
            organizationId,
            ...(leadId ? { leadId } : {}),
          },
          { onSuccess: setResult },
        );
      }}
    >
      <DialogBody>
        <TextField label="Project name" value={name} onChange={setName} isRequired autoFocus />
        <Select
          label="Client"
          options={organizationOptions}
          selectedKey={organizationId}
          onSelectionChange={setOrganizationId}
          description="Internal work names Matchbox, which is an organization like any other."
        />
        <Select
          label="Lead"
          options={leadOptions}
          selectedKey={leadId}
          onSelectionChange={setLeadId}
          description="A label on the record. It grants nothing — access is a grant on the project's space."
        />
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={name.trim() === '' || organizationId === ''}
        >
          Create project
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function ProjectListPage() {
  const projects = useProjects();
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Projects"
        description="The work, as rows rather than as folders. A project carries its own space and — for a client — the portal home beneath it; you see the ones you hold access to."
        actions={
          <Button variant="primary" onPress={() => setCreating(true)}>
            Create project
          </Button>
        }
      />

      <Card padded={false}>
        {projects.isError ? (
          <div className="p-5">
            <ErrorState error={projects.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Project</TH>
                <TH>Client</TH>
                <TH>Status</TH>
                <TH>Starts</TH>
                <TH>Ends</TH>
              </TR>
            </THead>
            <TBody>
              {projects.isPending ? (
                <TableEmpty colSpan={5}>Loading…</TableEmpty>
              ) : projects.data.projects.length === 0 ? (
                <TableEmpty colSpan={5}>
                  No projects you can reach. `project.view` in your role says what you may do; a
                  grant on a project's space says where.
                </TableEmpty>
              ) : (
                projects.data.projects.map((project) => (
                  <TR key={project.id} className="hover:bg-secondary">
                    <TD>
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: project.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {project.name}
                      </Link>
                    </TD>
                    <TD>
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: project.organizationId }}
                        className="text-sm text-tertiary no-underline"
                      >
                        {project.organization.name}
                      </Link>
                    </TD>
                    <TD>
                      <Badge tone={STATUS_TONES[project.status]}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap">{formatDate(project.startsOn)}</TD>
                    <TD className="whitespace-nowrap">{formatDate(project.endsOn)}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog
        title="Create a project"
        description="The project and its spaces are written together. Nobody gains access who was not named here."
        isOpen={creating}
        onOpenChange={setCreating}
      >
        {(close) => <CreateProjectForm onClose={close} />}
      </Dialog>
    </>
  );
}
